(() => {
  'use strict';

  const ENDPOINT = '/apps/brillo-nativo/student/dashboard';
  const ROOT_SELECTOR = '.bn-student-dashboard';

  const MODALITY_LABELS = {
    online: 'Curso online',
    presencial_grupal: 'Curso presencial grupal',
    presencial_personalizado: 'Curso personalizado',
  };

  const STATUS_LABELS = {
    not_authenticated: 'Visitante no autenticado',
    customer_not_found: 'Cliente sin sincronización',
    student_not_found: 'Perfil de alumno pendiente',
    student_without_courses: 'Alumno sin cursos activos',
    student_with_active_courses: 'Alumno con cursos activos',
  };

  const escapeHtml = (value) => {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  };

  const normalizeText = (value, fallback = '') => {
    const text = typeof value === 'string' ? value.trim() : '';

    return text || fallback;
  };

  const normalizeNumber = (value) => {
    const parsed = Number(value);

    return Number.isFinite(parsed) ? parsed : 0;
  };

  const getCourses = (payload) => {
    return Array.isArray(payload?.courses) ? payload.courses : [];
  };

  const getNextStep = (payload) => {
    const nextStep = payload?.nextStep && typeof payload.nextStep === 'object'
      ? payload.nextStep
      : {};

    return {
      code: normalizeText(nextStep.code, 'unknown'),
      label: normalizeText(nextStep.label, 'Revisa tu siguiente paso recomendado.'),
      href: normalizeText(nextStep.href, ''),
    };
  };

  const setRootState = (root, state) => {
    root.dataset.bnStudentProxyState = state;
    root.classList.remove(
      'bn-student-dashboard--proxy-loading',
      'bn-student-dashboard--proxy-active',
      'bn-student-dashboard--proxy-warning',
      'bn-student-dashboard--proxy-error'
    );

    if (state === 'active') {
      root.classList.add('bn-student-dashboard--proxy-active');
    } else if (state === 'warning') {
      root.classList.add('bn-student-dashboard--proxy-warning');
    } else if (state === 'error') {
      root.classList.add('bn-student-dashboard--proxy-error');
    } else {
      root.classList.add('bn-student-dashboard--proxy-loading');
    }
  };

  const findInner = (root) => {
    let inner = root.querySelector('.bn-student-dashboard__inner');

    if (!inner) {
      inner = document.createElement('div');
      inner.className = 'bn-student-dashboard__inner';
      root.appendChild(inner);
    }

    return inner;
  };

  const createProxyPanel = (root) => {
    const existingPanel = root.querySelector('[data-bn-student-dashboard-proxy]');

    if (existingPanel) {
      return existingPanel;
    }

    const inner = findInner(root);
    const panel = document.createElement('div');

    panel.className = 'bn-student-dashboard-proxy bn-student-dashboard__panel';
    panel.setAttribute('data-bn-student-dashboard-proxy', 'true');
    panel.setAttribute('aria-live', 'polite');
    panel.setAttribute('aria-atomic', 'true');

    inner.insertBefore(panel, inner.firstElementChild);

    return panel;
  };

  const renderLoading = (panel) => {
    panel.innerHTML = `
      <div class="bn-student-dashboard-proxy__head">
        <span class="bn-student-dashboard__badge">Conectando backend</span>
        <span class="bn-student-dashboard-proxy__dot" aria-hidden="true"></span>
      </div>
      <h2 class="bn-student-dashboard-proxy__title">Validando tu acceso académico</h2>
      <p class="bn-student-dashboard-proxy__text">
        Estamos consultando de forma segura tus accesos reales desde la app de Brillo Nativo.
      </p>
    `;
  };

  const renderError = (panel) => {
    panel.innerHTML = `
      <div class="bn-student-dashboard-proxy__head">
        <span class="bn-student-dashboard__badge">Conexión pendiente</span>
      </div>
      <h2 class="bn-student-dashboard-proxy__title">No pudimos validar el acceso en este momento</h2>
      <p class="bn-student-dashboard-proxy__text">
        El portal visual sigue disponible, pero la validación dinámica no respondió correctamente. Recarga la página o vuelve a intentarlo en unos minutos.
      </p>
    `;
  };

  const renderNotAuthenticated = (panel, payload) => {
    const nextStep = getNextStep(payload);
    const ctaHref = nextStep.href || '/account/login';

    panel.innerHTML = `
      <div class="bn-student-dashboard-proxy__head">
        <span class="bn-student-dashboard__badge">Backend conectado</span>
        <span class="bn-student-dashboard-proxy__status bn-student-dashboard-proxy__status--warning">
          ${escapeHtml(STATUS_LABELS.not_authenticated)}
        </span>
      </div>
      <h2 class="bn-student-dashboard-proxy__title">Inicia sesión para validar tus cursos</h2>
      <p class="bn-student-dashboard-proxy__text">
        El endpoint del alumno ya está activo. Para ver tus cursos reales, entra con el mismo correo usado en tu inscripción o compra.
      </p>
      <div class="bn-student-dashboard-proxy__actions">
        <a class="bn-student-dashboard__button" href="${escapeHtml(ctaHref)}" rel="nofollow">
          Iniciar sesión <span aria-hidden="true">→</span>
        </a>
      </div>
    `;
  };

  const renderNoAccess = (panel, payload) => {
    const status = normalizeText(payload?.studentStatus, 'student_without_courses');
    const nextStep = getNextStep(payload);
    const label = STATUS_LABELS[status] || 'Acceso pendiente';
    const ctaHref = nextStep.href || '/pages/soporte-alumnos';

    panel.innerHTML = `
      <div class="bn-student-dashboard-proxy__head">
        <span class="bn-student-dashboard__badge">Backend conectado</span>
        <span class="bn-student-dashboard-proxy__status bn-student-dashboard-proxy__status--warning">
          ${escapeHtml(label)}
        </span>
      </div>
      <h2 class="bn-student-dashboard-proxy__title">Tu cuenta aún no tiene cursos activos</h2>
      <p class="bn-student-dashboard-proxy__text">
        ${escapeHtml(nextStep.label)}
      </p>
      <div class="bn-student-dashboard-proxy__summary" aria-label="Resumen backend">
        <div class="bn-student-dashboard-proxy__metric">
          <span>Sesión cliente</span>
          <strong>${payload?.loggedIn ? 'Activa' : 'No iniciada'}</strong>
        </div>
        <div class="bn-student-dashboard-proxy__metric">
          <span>Cursos activos</span>
          <strong>${escapeHtml(normalizeNumber(payload?.accessCount))}</strong>
        </div>
        <div class="bn-student-dashboard-proxy__metric">
          <span>Estado</span>
          <strong>${escapeHtml(label)}</strong>
        </div>
      </div>
      <div class="bn-student-dashboard-proxy__actions">
        <a class="bn-student-dashboard__button bn-student-dashboard__button--secondary" href="${escapeHtml(ctaHref)}">
          Continuar <span aria-hidden="true">→</span>
        </a>
      </div>
    `;
  };

  const renderCourseCard = (course) => {
    const title = normalizeText(course?.title, 'Curso Brillo Nativo');
    const modality = normalizeText(course?.modality, 'curso');
    const modalityLabel = MODALITY_LABELS[modality] || modality.replaceAll('_', ' ');
    const level = normalizeText(course?.level, 'Nivel no definido');
    const description = normalizeText(course?.description, 'Acceso activo disponible para este alumno.');
    const accessStatus = normalizeText(course?.accessStatus, 'active');
    const nextUrl = normalizeText(course?.nextUrl, '/pages/centro-de-aprendizaje');

    return `
      <article class="bn-student-dashboard-proxy__course">
        <div>
          <span class="bn-student-dashboard-proxy__course-pill">${escapeHtml(modalityLabel)}</span>
          <h3>${escapeHtml(title)}</h3>
          <p>${escapeHtml(description)}</p>
          <div class="bn-student-dashboard-proxy__course-meta">
            <span>Nivel: ${escapeHtml(level)}</span>
            <span>Estado: ${escapeHtml(accessStatus)}</span>
          </div>
        </div>
        <a class="bn-student-dashboard__button bn-student-dashboard__button--secondary" href="${escapeHtml(nextUrl)}">
          Abrir curso <span aria-hidden="true">→</span>
        </a>
      </article>
    `;
  };

  const hideLockedFallbacks = (root) => {
    const fallbackPanels = root.querySelectorAll(
      '.bn-student-dashboard__locked, .bn-student-dashboard__gate'
    );

    fallbackPanels.forEach((fallbackPanel) => {
      const proxyPanel = fallbackPanel.closest('[data-bn-student-dashboard-proxy]');

      if (!proxyPanel) {
        fallbackPanel.hidden = true;
        fallbackPanel.setAttribute('data-bn-hidden-by-proxy', 'true');
      }
    });
  };

  const restoreLockedFallbacks = (root) => {
    const fallbackPanels = root.querySelectorAll('[data-bn-hidden-by-proxy="true"]');

    fallbackPanels.forEach((fallbackPanel) => {
      fallbackPanel.hidden = false;
      fallbackPanel.removeAttribute('data-bn-hidden-by-proxy');
    });
  };

  const updateStaticCourseCounters = (root, accessCount) => {
    const statLabels = Array.from(root.querySelectorAll('.bn-student-dashboard__stat-label'));

    statLabels.forEach((label) => {
      const labelText = label.textContent.trim().toLowerCase();

      if (!labelText.includes('cursos detectados')) {
        return;
      }

      const stat = label.closest('.bn-student-dashboard__stat');
      const value = stat?.querySelector('.bn-student-dashboard__stat-value > span:first-child');
      const context = stat?.querySelector('.bn-student-dashboard__stat-context');
      const note = stat?.querySelector('.bn-student-dashboard__stat-note');

      if (value) {
        value.textContent = String(accessCount);
      }

      if (context) {
        context.textContent = accessCount === 1 ? 'curso activo' : 'cursos activos';
      }

      if (note) {
        note.textContent = 'Validado desde backend/app y Supabase.';
      }
    });

    const sectionPills = Array.from(root.querySelectorAll('.bn-student-dashboard__section-pill'));

    sectionPills.forEach((pill) => {
      if (pill.textContent.toLowerCase().includes('curso')) {
        pill.textContent = `${accessCount} ${accessCount === 1 ? 'curso activo' : 'cursos activos'}`;
      }
    });
  };

  const renderActive = (root, panel, payload) => {
    const courses = getCourses(payload);
    const accessCount = normalizeNumber(payload?.accessCount);
    const student = payload?.student && typeof payload.student === 'object' ? payload.student : {};
    const displayName = normalizeText(student.displayName, 'Alumno Brillo Nativo');
    const studentStatus = normalizeText(student.status, 'active');
    const nextStep = getNextStep(payload);
    const nextHref = nextStep.href || '/pages/mis-cursos-digitales';
    const coursesMarkup = courses.length
      ? courses.map(renderCourseCard).join('')
      : `
        <div class="bn-student-dashboard-proxy__empty">
          No hay cursos activos para mostrar en este momento.
        </div>
      `;

    hideLockedFallbacks(root);
    updateStaticCourseCounters(root, accessCount);

    panel.innerHTML = `
      <div class="bn-student-dashboard-proxy__head">
        <span class="bn-student-dashboard__badge">Backend conectado</span>
        <span class="bn-student-dashboard-proxy__status bn-student-dashboard-proxy__status--success">
          Acceso validado
        </span>
      </div>

      <div class="bn-student-dashboard-proxy__hero">
        <div>
          <h2 class="bn-student-dashboard-proxy__title">Acceso académico activo</h2>
          <p class="bn-student-dashboard-proxy__text">
            Hola, ${escapeHtml(displayName)}. Tus cursos fueron validados desde la app de Brillo Nativo, usando App Proxy, Shopify Customer ID y Supabase.
          </p>
        </div>
        <div class="bn-student-dashboard-proxy__seal" aria-hidden="true">
          ✓
        </div>
      </div>

      <div class="bn-student-dashboard-proxy__summary" aria-label="Resumen dinámico del alumno">
        <div class="bn-student-dashboard-proxy__metric">
          <span>Sesión cliente</span>
          <strong>Activa</strong>
        </div>
        <div class="bn-student-dashboard-proxy__metric">
          <span>Perfil alumno</span>
          <strong>${escapeHtml(studentStatus)}</strong>
        </div>
        <div class="bn-student-dashboard-proxy__metric">
          <span>Cursos activos</span>
          <strong>${escapeHtml(accessCount)}</strong>
        </div>
      </div>

      <div class="bn-student-dashboard-proxy__courses" aria-label="Cursos activos validados por backend">
        ${coursesMarkup}
      </div>

      <div class="bn-student-dashboard-proxy__actions">
        <a class="bn-student-dashboard__button" href="${escapeHtml(nextHref)}">
          ${escapeHtml(nextStep.label || 'Continuar aprendiendo')} <span aria-hidden="true">→</span>
        </a>
        <a class="bn-student-dashboard__button bn-student-dashboard__button--secondary" href="/pages/soporte-alumnos">
          Soporte alumnos
        </a>
      </div>
    `;
  };

  const renderPayload = (root, panel, payload) => {
    const status = normalizeText(payload?.studentStatus, 'unknown');

    if (!payload?.ok) {
      setRootState(root, 'error');
      renderError(panel);
      return;
    }

    if (status === 'not_authenticated') {
      restoreLockedFallbacks(root);
      setRootState(root, 'warning');
      renderNotAuthenticated(panel, payload);
      return;
    }

    if (status === 'student_with_active_courses') {
      setRootState(root, 'active');
      renderActive(root, panel, payload);
      return;
    }

    restoreLockedFallbacks(root);
    setRootState(root, 'warning');
    renderNoAccess(panel, payload);
  };

  const fetchDashboard = async () => {
    const response = await fetch(ENDPOINT, {
      method: 'GET',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Dashboard request failed with status ${response.status}`);
    }

    return response.json();
  };

  const mountRoot = async (root) => {
    if (!root || root.dataset.bnStudentProxyMounted === 'true') {
      return;
    }

    root.dataset.bnStudentProxyMounted = 'true';

    const panel = createProxyPanel(root);

    setRootState(root, 'loading');
    renderLoading(panel);

    try {
      const payload = await fetchDashboard();
      renderPayload(root, panel, payload);
    } catch (error) {
      console.error('[Brillo Nativo] Error consultando dashboard de alumno:', error);
      setRootState(root, 'error');
      renderError(panel);
    }
  };

  const init = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach(mountRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();